import { useFormContext } from "react-hook-form";
import { useEffect, useRef } from "react";
import CheckboxField from "./CheckboxField";
import NumberSelectField from "./NumberSelectField";

const MAX_ITEMS = 3;
const DEFAULT_QUANTITY = 1;
const QUANTITY_FIELDS = ["clayImpressions", "inkPrints", "furClippings"];

function RemembranceItems() {
  const {
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  const isScatteringService = watch("aquamationType") === "Scattering Service";
  const watchExcludeRemembranceItems = watch("excludeRemembranceItems");

  // Watch quantity fields for total calculation
  const clayImpressions = watch("clayImpressions") || 0;
  const inkPrints = watch("inkPrints") || 0;
  const furClippings = watch("furClippings") || 0;
  const totalItems = clayImpressions + inkPrints + furClippings;

  const isDisabled = isScatteringService || watchExcludeRemembranceItems;
  const isAtLimit = totalItems >= MAX_ITEMS;

  useEffect(() => {
    if (isScatteringService) {
      setValue("excludeRemembranceItems", false);
    }
  }, [isScatteringService, setValue]);

  // Reset quantities when disabled, and restore the defaults when the fields
  // become editable again — otherwise unchecking the opt-out (or leaving
  // scattering service) would leave every quantity at 0, which auto-checks the
  // opt-out straight back on.
  const wasDisabled = useRef(isDisabled);
  useEffect(() => {
    if (isDisabled) {
      QUANTITY_FIELDS.forEach((field) => setValue(field, 0));
    } else if (wasDisabled.current) {
      QUANTITY_FIELDS.forEach((field) => setValue(field, DEFAULT_QUANTITY));
    }
    wasDisabled.current = isDisabled;
  }, [isDisabled, setValue]);

  // Zeroing out every item means the family wants none, so check the opt-out for
  // them. This tracks the drop to 0 rather than the total being 0, so the
  // restore above (which lands a render later) can't retrigger it.
  const prevTotalItems = useRef(totalItems);
  useEffect(() => {
    const droppedToZero = prevTotalItems.current > 0 && totalItems === 0;
    prevTotalItems.current = totalItems;

    if (isScatteringService || watchExcludeRemembranceItems) return;
    if (droppedToZero) {
      setValue("excludeRemembranceItems", true);
    }
  }, [totalItems, isScatteringService, watchExcludeRemembranceItems, setValue]);

  return (
    <fieldset>
      <label>Remembrance Items</label>
      <div>
        {isScatteringService ? (
          <p>
            Remembrance items are not included with scattering service. They can
            be purchased using the link in the email the pet owner receives.
          </p>
        ) : (
          <>
            {!watchExcludeRemembranceItems && (
              <div>
                <div className="remembrance-items-toggles">
                  <NumberSelectField
                    name="clayImpressions"
                    label="Clay paw impressions"
                    required
                    disabled={isDisabled}
                    disabledIncrement={isAtLimit}
                  />
                  <NumberSelectField
                    name="inkPrints"
                    label="Ink paw prints"
                    required
                    disabled={isDisabled}
                    disabledIncrement={isAtLimit}
                  />
                  <NumberSelectField
                    name="furClippings"
                    label="Fur clippings"
                    required
                    disabled={isDisabled}
                    disabledIncrement={isAtLimit}
                  />
                </div>
                <p>
                  {totalItems} of {MAX_ITEMS} items selected. Additional items
                  can be purchased in the email the pet owner receives.
                </p>
              </div>
            )}
            <CheckboxField
              name="excludeRemembranceItems"
              label={"Family would like to opt-out of all remembrance items"}
            />
          </>
        )}
      </div>
      <span className="input-error">
        {errors.excludeRemembranceItems?.message}
      </span>
    </fieldset>
  );
}

export default RemembranceItems;
